<template>
    <div>
        <!-- Botón flotante -->
        <v-btn
            fab
            fixed
            bottom
            right
            small
            depressed
            color="primary"
            class="ai-chat-fab black--text"
            :class="{ 'ai-chat-fab--footer': hasFooter && isMobile }"
            @click="onFabClick"
        >
            <v-badge overlap bottom left color="accent" content="β" :value="betaVisible">
                <v-icon>mdi-robot-outline</v-icon>
            </v-badge>
        </v-btn>

        <!-- Drawer lateral -->
        <v-navigation-drawer
            v-model="drawer"
            app
            right
            temporary
            width="380"
            class="ai-chat-drawer"
        >
            <div class="ai-panel">
                <!-- Header -->
                <div class="ai-chat-header">
                    <div class="ai-avatar">
                        <v-icon size="20" color="#0C0E12">mdi-robot-outline</v-icon>
                    </div>
                    <div class="ai-header-text">
                        <div class="ai-title">
                            {{ $t('ai.chat_title') }}
                            <ai-beta-chip class="ml-1" />
                        </div>
                        <div class="ai-sub" v-if="activeStudentName">
                            {{ activeStudentName }}
                            <v-btn
                                v-if="!studentId"
                                x-small text
                                color="accent"
                                class="ml-1 pa-0 ai-change-btn"
                                @click="backToSelector"
                            >
                                {{ $t('ai.chat_change_student') }}
                            </v-btn>
                        </div>
                    </div>
                    <v-btn icon small class="ai-icon-btn" @click="drawer = false">
                        <v-icon size="18">mdi-close</v-icon>
                    </v-btn>
                </div>

                <!-- Selector de alumno (solo cuando no hay studentId fijo) -->
                <div v-if="!studentId && !activeStudentId" class="ai-selector">
                    <div class="ai-muted mb-3">{{ $t('ai.chat_select_student') }}</div>
                    <div v-if="loadingStudents" class="text-center py-4">
                        <v-progress-circular indeterminate color="primary" size="28" />
                    </div>
                    <v-list dense v-else class="pa-0 transparent">
                        <v-list-item
                            v-for="s in availableStudents"
                            :key="s.objectID"
                            @click="selectStudent(s)"
                            class="student-item mb-1"
                        >
                            <v-list-item-avatar size="32" tile class="student-avatar">
                                <v-img v-if="s.avatar" :src="s.avatar" />
                                <div v-else class="student-initials" :style="avatarGradient(s)">{{ initials(s) }}</div>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title class="student-name">{{ s.name || s.email }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-icon small class="ai-muted-icon">mdi-chevron-right</v-icon>
                            </v-list-item-action>
                        </v-list-item>
                        <div v-if="availableStudents.length === 0" class="text-center ai-muted py-4">
                            {{ $t('common.not_found') }}
                        </div>
                    </v-list>
                </div>

                <!-- Chat (cuando hay alumno activo) -->
                <template v-if="activeStudentId">
                    <!-- Messages -->
                    <div class="ai-chat-messages" ref="messagesContainer">
                        <div v-if="loading && messages.length === 0" class="text-center mt-6">
                            <v-progress-circular indeterminate color="primary" size="32" />
                        </div>

                        <div v-else-if="messages.length === 0" class="text-center mt-6 px-4">
                            <v-icon size="48" class="ai-muted-icon">mdi-robot-outline</v-icon>
                            <p class="ai-muted mt-3">{{ $t('ai.chat_empty') }}</p>
                            <ai-beta-note />
                        </div>

                        <div
                            v-for="(msg, i) in messages"
                            :key="i"
                            class="d-flex"
                            :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
                        >
                            <div
                                class="ai-bubble"
                                :class="msg.role === 'user' ? 'ai-bubble--user' : 'ai-bubble--assistant'"
                            >{{ msg.content }}</div>
                        </div>

                        <!-- Typing indicator -->
                        <div v-if="thinking" class="d-flex justify-start">
                            <div class="ai-bubble ai-bubble--assistant">
                                <v-progress-circular indeterminate color="primary" size="16" width="2" />
                            </div>
                        </div>
                    </div>

                    <!-- Input -->
                    <div class="ai-chat-input">
                        <div class="ai-input-box">
                            <v-text-field
                                v-model="input"
                                :placeholder="$t('ai.chat_placeholder')"
                                solo
                                flat
                                dense
                                hide-details
                                background-color="transparent"
                                class="ai-input"
                                :disabled="thinking"
                                @keydown.enter.prevent="send"
                            />
                            <v-btn
                                depressed
                                class="ai-send-btn"
                                :disabled="!input.trim() || thinking"
                                :loading="thinking"
                                @click="send"
                            >
                                <v-icon size="16" color="#0C0E12">mdi-send</v-icon>
                            </v-btn>
                        </div>
                        <div class="d-flex align-center mt-1">
                            <ai-beta-note class="mt-0" />
                            <v-spacer />
                            <v-btn
                                x-small text
                                class="ai-clear-btn"
                                :disabled="messages.length === 0 || thinking"
                                @click="confirmClear"
                            >
                                <v-icon x-small class="mr-1">mdi-delete-outline</v-icon>
                                {{ $t('ai.chat_clear') }}
                            </v-btn>
                        </div>
                    </div>
                </template>
            </div>
        </v-navigation-drawer>

        <!-- Confirm clear -->
        <kimsa-confirm
            ref="confirmClearDialog"
            :title="$t('ai.chat_clear_title')"
            :content="$t('ai.chat_clear_body')"
            :persistent="false"
            @confirm="clearHistory"
        />
    </div>
</template>

<script>
import { mapState } from 'vuex'
import KimsaConfirm from '@/components/common/KimsaConfirm'
import AiBetaChip from '@/components/coach/ai/AiBetaChip'
import AiBetaNote from '@/components/coach/ai/AiBetaNote'
import firebase from 'firebase/app'
import 'firebase/functions'
import {
    loadChatHistory,
    saveMessage,
    clearChatHistory,
    sendMessageToAI,
    buildStudentContext,
} from '@/utils/AiChat'
import { UserIndex } from '@/utils/Algolia'
import { getInitials, getAvatarGradient } from '@/utils/Common'

export default {
    name: 'AiChatDrawer',
    components: { KimsaConfirm, AiBetaChip, AiBetaNote },

    props: {
        // Si se pasa studentId, el chat es fijo para ese alumno (desde StudentProfile)
        // Si no se pasa, muestra el selector (desde Home)
        studentId: {
            type: String,
            default: null,
        },
        student: {
            type: Object,
            default: null,
        },
        monthRoutines: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            drawer: false,
            messages: [],
            input: '',
            loading: false,
            thinking: false,
            // Estado interno cuando se usa sin studentId fijo
            activeStudentId: null,
            activeStudent: null,
            availableStudents: [],
            loadingStudents: false,
        }
    },

    computed: {
        ...mapState(['currentUser', 'account', 'config']),
        betaVisible() {
            return this.config?.aiBeta !== false
        },
        coachId() {
            return this.currentUser?.uid
        },
        // El alumno activo puede venir del prop o de la selección interna
        effectiveStudentId() {
            return this.studentId || this.activeStudentId
        },
        activeStudentName() {
            if (this.student?.name) return this.student.name
            return this.activeStudent?.name || this.activeStudent?.email || ''
        },
        hasFooter() {
            return !!this.$route?.meta?.footer
        },
        locale() {
            return this.$i18n?.locale || 'es'
        },
    },

    watch: {
        // Si desde la tabla se cambia el alumno mientras el drawer está abierto
        studentId(newId) {
            if (newId && newId !== this.activeStudentId) {
                this.switchToStudent(newId, this.student)
            }
        },
    },

    methods: {
        initials(student) {
            return getInitials(student.name || student.email)
        },
        avatarGradient(student) {
            return getAvatarGradient(student.objectID || student.name)
        },
        // Llamado desde el botón flotante
        async onFabClick() {
            this.drawer = true
            if (this.studentId) {
                // Modo perfil — carga directo
                await this.openChat(this.studentId, this.student)
            } else {
                // Modo home — muestra selector si no hay alumno activo
                if (!this.activeStudentId) {
                    await this.loadStudentList()
                } else {
                    this.$nextTick(() => this.scrollToBottom())
                }
            }
        },

        // Llamado desde el botón de chat por fila en la tabla

        async openChat(studentId, student = null) {
            this.activeStudentId = studentId
            this.activeStudent = student
            this.messages = []
            await this.loadHistory()
            this.$nextTick(() => this.scrollToBottom())
        },

        async switchToStudent(studentId, student = null) {
            if (this.activeStudentId === studentId) {
                this.$nextTick(() => this.scrollToBottom())
                return
            }
            this.activeStudentId = studentId
            this.activeStudent = student
            this.messages = []
            await this.loadHistory()
            this.$nextTick(() => this.scrollToBottom())
        },

        async loadStudentList() {
            if (!this.coachId) return
            this.loadingStudents = true
            try {
                const filter = `studentSubscription.coachId: ${this.coachId} AND studentSubscription.paused != 1`
                const response = await UserIndex.search('', { filters: filter, hitsPerPage: 1000 })
                this.availableStudents = response.hits
            } catch (e) {
                console.warn('[AiChatDrawer] Error cargando alumnos:', e?.message)
            } finally {
                this.loadingStudents = false
            }
        },

        async selectStudent(student) {
            await this.openChat(student.objectID, student)
        },

        backToSelector() {
            this.activeStudentId = null
            this.activeStudent = null
            this.messages = []
            this.loadStudentList()
        },

        async loadHistory() {
            if (!this.coachId || !this.effectiveStudentId) return
            this.loading = true
            try {
                this.messages = await loadChatHistory(this.coachId, this.effectiveStudentId)
            } catch (e) {
                console.warn('[AiChatDrawer] Error cargando historial:', e?.message)
            } finally {
                this.loading = false
            }
        },

        async send() {
            const text = this.input.trim()
            if (!text || this.thinking) return

            if (text.startsWith('/')) {
                await this.handleCommand(text)
                return
            }

            this.input = ''
            const userMessage = { role: 'user', content: text }
            this.messages.push(userMessage)
            this.thinking = true
            this.$nextTick(() => this.scrollToBottom())

            try {
                await saveMessage(this.coachId, this.effectiveStudentId, 'user', text)
                const context = buildStudentContext(this.student || this.activeStudent, this.monthRoutines)
                const aiText = await sendMessageToAI(this.messages, context, this.locale, this.effectiveStudentId)
                const assistantMessage = { role: 'assistant', content: aiText }
                this.messages.push(assistantMessage)
                await saveMessage(this.coachId, this.effectiveStudentId, 'assistant', aiText)
            } catch (e) {
                console.error('[AiChatDrawer] Error:', e?.message)
                this.messages.push({
                    role: 'assistant',
                    content: this.$t('ai.chat_error'),
                })
            } finally {
                this.thinking = false
                this.$nextTick(() => this.scrollToBottom())
            }
        },

        async handleCommand(cmd) {
            const vm = this
            const command = cmd.toLowerCase().trim()
            vm.input = ''

            const commandMessages = {
                '/recomendacion': '🤖 Generando recomendación mensual...',
                '/riesgo': '🤖 Analizando riesgo de lesión...',
            }

            const validCommands = Object.keys(commandMessages)
            if (!validCommands.includes(command)) {
                vm.messages.push({
                    role: 'assistant',
                    content: `Comando no reconocido. Comandos disponibles:\n${validCommands.join('\n')}`,
                })
                return
            }

            vm.messages.push({ role: 'user', content: cmd })
            vm.messages.push({ role: 'assistant', content: commandMessages[command] })
            vm.thinking = true
            vm.$nextTick(() => vm.scrollToBottom())

            try {
                const functions = firebase.app().functions('us-central1')

                if (command === '/recomendacion') {
                    const fn = functions.httpsCallable('aiGenerateRecommendation')
                    const result = await fn({ studentId: vm.effectiveStudentId })
                    vm.messages[vm.messages.length - 1].content =
                        `✅ Recomendación generada para ${result.data.month}. Recargá el perfil para verla.`
                }

                if (command === '/riesgo') {
                    const fn = functions.httpsCallable('aiAnalyzeInjuryRisk')
                    const result = await fn({ studentId: vm.effectiveStudentId })
                    const nivel = result.data.riskLevel
                    if (nivel === 'none') {
                        vm.messages[vm.messages.length - 1].content =
                            '✅ Análisis completado. No se detectaron señales de riesgo esta semana.'
                    } else {
                        vm.messages[vm.messages.length - 1].content =
                            `⚠️ Riesgo ${nivel.toUpperCase()} detectado. Recargá el perfil para ver el detalle.`
                    }
                }
            } catch (e) {
                console.error('[AiChatDrawer] Error en comando:', e?.message)
                vm.messages[vm.messages.length - 1].content =
                    '❌ Error al ejecutar el comando. Intentá de nuevo.'
            } finally {
                vm.thinking = false
                vm.$nextTick(() => vm.scrollToBottom())
            }
        },

        confirmClear() {
            this.$refs.confirmClearDialog.show()
        },

        async clearHistory() {
            try {
                await clearChatHistory(this.coachId, this.effectiveStudentId)
                this.messages = []
            } catch (e) {
                console.warn('[AiChatDrawer] Error limpiando historial:', e?.message)
            }
        },

        scrollToBottom() {
            const el = this.$refs.messagesContainer
            if (el) el.scrollTop = el.scrollHeight
        },
    },
}
</script>

<style scoped>
.ai-chat-fab {
    z-index: 6;
    right: 16px;
    bottom: 16px;
}

.ai-chat-fab--footer {
    bottom: calc(12px + 74px + var(--safe-area-inset-bottom, 0px));
}

.ai-chat-drawer {
    max-width: 100vw;
    background: var(--v-surface-base) !important;
    border-left: 1px solid var(--v-outline-base);
}

.ai-chat-drawer >>> .v-navigation-drawer__content {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.ai-panel {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--v-surface-base);
    overflow: hidden;
}

.ai-chat-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 60px;
    padding: calc(8px + var(--safe-area-inset-top, 0px)) 14px 8px;
    border-bottom: 1px solid var(--v-outline-base);
}

.ai-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--brand-gradient);
}

.ai-header-text {
    flex: 1;
    min-width: 0;
}

.ai-title {
    font-size: 14px;
    font-weight: 700;
    line-height: 1.3;
    color: var(--v-txtPrimary-base);
}

.ai-sub {
    font-size: 11px;
    line-height: 1.3;
    color: var(--v-txtMuted-base);
}

.ai-change-btn {
    min-width: 0 !important;
    height: auto !important;
    font-size: 11px;
    font-weight: 600;
    text-transform: none;
}

.ai-icon-btn,
.ai-muted-icon {
    color: var(--v-txtMuted-base) !important;
}

.ai-muted {
    font-size: 13px;
    color: var(--v-txtMuted-base);
}

.ai-selector {
    padding: 16px;
    overflow-y: auto;
}

.ai-chat-messages {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.ai-chat-input {
    flex-shrink: 0;
    padding: 12px 14px calc(12px + var(--safe-area-inset-bottom, 0px));
    border-top: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
}

.ai-bubble {
    max-width: 75%;
    padding: 9px 13px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.5;
    word-break: break-word;
    white-space: pre-wrap;
    color: var(--v-txtPrimary-base);
}

.ai-bubble--user {
    background: rgba(51, 153, 153, 0.18);
    border: 1px solid rgba(51, 153, 153, 0.25);
    border-bottom-right-radius: 4px;
}

.ai-bubble--assistant {
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-bottom-left-radius: 4px;
}

.ai-input-box {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 6px 4px 12px;
    background: var(--v-field-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
}

.ai-input-box:focus-within {
    border-color: var(--v-accent-base);
}

.ai-input {
    flex: 1;
    font-size: 13px;
    margin: 0;
    padding: 0;
}

.ai-input >>> .v-input__slot {
    min-height: 32px !important;
    padding: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
}

.ai-input >>> input {
    font-size: 13px;
    color: var(--v-txtPrimary-base);
}

.ai-send-btn {
    width: 32px !important;
    min-width: 32px !important;
    height: 32px !important;
    padding: 0 !important;
    border-radius: 8px;
    background: var(--brand-gradient) !important;
}

.ai-send-btn.v-btn--disabled {
    opacity: 0.45;
}

.ai-send-btn.v-btn--disabled .v-icon {
    color: #0C0E12 !important;
}

.student-avatar {
    border-radius: 8px;
}

.student-initials {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 700;
}

.ai-clear-btn {
    color: var(--v-txtMuted-base) !important;
    text-transform: none;
}

.student-item {
    border-radius: 8px;
    cursor: pointer;
    background: transparent;
}

.student-item:hover {
    background-color: var(--v-surface2-base);
}

.student-name {
    font-size: 13px;
    color: var(--v-txtPrimary-base);
}
</style>
